import { useGetDeviceDetailsInStateStore } from '@unifyapps/carbon/no-code/stores/GlobalStateStore';
import type { EmitEventType } from '@unifyapps/defs/hooks/useBlockEvents';
import type { BlockType } from '@unifyapps/defs/types/block';
import { DeviceVariantType } from '@unifyapps/defs/types/deviceVariant';
import { type Event, EventTypeEnum } from '@unifyapps/defs/types/event';
import { useLongPress } from '@unifyapps/hooks/useLongPress';
import { type SyntheticEvent, useCallback, useMemo } from 'react';
import { isEventHandledByBlock, markEventAsHandledByBlock } from '../utils/eventHandling';

export interface UseBlockInteractionsParams {
  events?: Event[];
  emitEvent: EmitEventType;
  blockType?: BlockType['component']['componentType'];
}

const MARK_EVENT_AS_HANDLED_BY_BLOCK_SET = new Set(['Button']);

export function useBlockInteractions({ events, emitEvent, blockType }: UseBlockInteractionsParams) {
  const { device } = useGetDeviceDetailsInStateStore();

  const { hasClickEvent, hasMouseEnterEvent, hasLongPressEvent } = useMemo(() => {
    let hasClick = false;
    let hasMouseEnter = false;
    let hasLongPress = false;

    events?.forEach((event) => {
      switch (event.eventType) {
        case EventTypeEnum.OnClick:
          hasClick = true;
          break;
        case EventTypeEnum.OnMouseEnter:
          hasMouseEnter = true;
          break;
        case EventTypeEnum.OnLongPress:
          hasLongPress = true;
          break;
        default:
          break;
      }
    });

    return {
      hasClickEvent: hasClick,
      hasMouseEnterEvent: hasMouseEnter,
      hasLongPressEvent: hasLongPress,
    };
  }, [events]);

  const onLongPress = useCallback(() => {
    void emitEvent({ eventType: EventTypeEnum.OnLongPress });
  }, [emitEvent]);

  const { longPressOccurredRef, longPressProps } = useLongPress(onLongPress);

  const onClick = useCallback(
    (e: SyntheticEvent) => {
      if (longPressOccurredRef.current) {
        longPressOccurredRef.current = false;
        return;
      }

      // a nested leaf control (Toggle, Checkbox, Button, IconButton...) marks the click
      // as handled before it bubbles here. honour that marker so clicking the control does
      // not also fire this container's On Click — same contract the Card collapse toggle uses.
      if (isEventHandledByBlock(e)) {
        return;
      }

      if (blockType && MARK_EVENT_AS_HANDLED_BY_BLOCK_SET.has(blockType)) {
        markEventAsHandledByBlock({ blockType, event: e });
      }

      void emitEvent({
        eventType: EventTypeEnum.OnClick,
        domEvent: e,
        actionContext: { domEvent: e },
      });
    },
    [emitEvent, longPressOccurredRef, blockType],
  );

  const onMouseEnter = useCallback(
    (e: SyntheticEvent) => {
      void emitEvent({
        eventType: EventTypeEnum.OnMouseEnter,
        domEvent: e,
        actionContext: { domEvent: e },
      });
    },
    [emitEvent],
  );

  const allowLongPressEvent =
    (device === DeviceVariantType.MOBILE || device === DeviceVariantType.TABLET) &&
    hasLongPressEvent;

  return {
    onClick: hasClickEvent ? onClick : undefined,
    onMouseEnter: hasMouseEnterEvent ? onMouseEnter : undefined,
    longPressProps: allowLongPressEvent ? longPressProps : undefined,
  };
}
